import { useEffect, useCallback } from "react";
import { IconClose } from "./icons";

const ChevronLeft = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

/**
 * Lightbox
 * @param {object[]} images  - Array of { src, alt }
 * @param {number|null} index - Currently open index (null = closed)
 * @param {function} onClose
 * @param {function} onNav   - Called with next index
 */
export default function Lightbox({ images, index, onClose, onNav }) {
  const hasMultiple = images && images.length > 1;
  const current = images && index !== null ? images[index] : null;

  const handlePrev = useCallback(() => {
    onNav((index - 1 + images.length) % images.length);
  }, [index, images, onNav]);

  const handleNext = useCallback(() => {
    onNav((index + 1) % images.length);
  }, [index, images, onNav]);

  useEffect(() => {
    if (index === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) handlePrev();
      if (e.key === "ArrowRight" && hasMultiple) handleNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [index, onClose, handlePrev, handleNext, hasMultiple]);

  if (index === null || !current) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9000,
        background: "rgba(14, 6, 16, 0.92)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        animation: "lb-fade-in 0.25s ease",
      }}
    >
      <style>{`
        @keyframes lb-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes lb-img-in { from { opacity: 0; transform: scale(0.96) } to { opacity: 1; transform: scale(1) } }
      `}</style>

      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: "20px", right: "20px",
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
          color: "var(--white)", borderRadius: "50%", width: "44px", height: "44px",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.2s",
          zIndex: 1,
        }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(212,168,83,0.25)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
      >
        <IconClose />
      </button>

      {/* Prev */}
      {hasMultiple && (
        <button
          onClick={e => { e.stopPropagation(); handlePrev(); }}
          style={{
            position: "absolute", left: "16px",
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
            color: "var(--white)", borderRadius: "50%", width: "48px", height: "48px",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(212,168,83,0.25)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
        >
          <ChevronLeft />
        </button>
      )}

      {/* Media */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: "min(90vw, 1000px)", maxHeight: "88vh", display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}
      >
        {current.type === "video" ? (
          <video
            key={index}
            src={current.src}
            controls
            autoPlay
            muted
            playsInline
            style={{
              maxWidth: "100%", maxHeight: "80vh",
              borderRadius: "var(--radius-lg)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
              animation: "lb-img-in 0.3s ease",
              background: "#000",
            }}
          />
        ) : (
          <img
            key={index}
            src={current.src}
            alt={current.alt}
            style={{
              maxWidth: "100%", maxHeight: "80vh",
              objectFit: "contain", borderRadius: "var(--radius-lg)",
              boxShadow: "0 24px 80px rgba(0,0,0,0.7)",
              animation: "lb-img-in 0.3s ease",
            }}
          />
        )}
        {hasMultiple && (
          <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.82rem", letterSpacing: "0.06em" }}>
            {index + 1} / {images.length}
          </span>
        )}
      </div>

      {/* Next */}
      {hasMultiple && (
        <button
          onClick={e => { e.stopPropagation(); handleNext(); }}
          style={{
            position: "absolute", right: "16px",
            background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
            color: "var(--white)", borderRadius: "50%", width: "48px", height: "48px",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            transition: "background 0.2s",
          }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(212,168,83,0.25)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
        >
          <ChevronRight />
        </button>
      )}
    </div>
  );
}
